import React, { useState, useMemo } from 'react';
import { Save, Plus, Trash2, Pencil, ChevronUp, ChevronDown } from 'lucide-react';
import { apiCall, useAdminData, ImageUploader } from './_helpers';

const blank = (defaultCategory = '', order = 0) => ({
  name: '',
  description: '',
  category: defaultCategory,
  image: '',
  is_active: true,
  order,
});

const MenuHighlightsManager = ({ onMessage }) => {
  const [items, setItems, refetch] = useAdminData('menu-highlights');
  const [menuItems] = useAdminData('menu-items');
  const [editing, setEditing] = useState(null); // null | { ...item, _id?: string }
  const [creating, setCreating] = useState(null);

  // Derive available categories from the live Menu Items collection, ordered by
  // section_order so they appear in the same order as the public /menu page.
  const categories = useMemo(() => {
    const map = new Map();
    menuItems.forEach((it) => {
      const cat = it.category || 'Uncategorized';
      const so = it.section_order ?? 999;
      const existing = map.get(cat);
      if (!existing || so < existing.section_order) {
        map.set(cat, { name: cat, section_order: so });
      }
    });
    return Array.from(map.values())
      .sort((a, b) => a.section_order - b.section_order || a.name.localeCompare(b.name))
      .map((c) => c.name);
  }, [menuItems]);

  // Sort items by `order` so the admin list mirrors the homepage display order.
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [items]
  );

  const handleSave = async (value, isNew) => {
    try {
      if (isNew) {
        const maxOrder = items.reduce((m, i) => Math.max(m, i.order ?? 0), -1);
        const payload = { ...value, order: value.order ?? maxOrder + 1 };
        await apiCall('POST', 'menu-highlights', payload);
        onMessage('success', 'Saved');
        setCreating(null);
      } else {
        const { _id, created_at, updated_at, ...payload } = value;
        await apiCall('PUT', `menu-highlights/${_id}`, payload);
        onMessage('success', 'Updated');
        setEditing(null);
      }
      refetch();
    } catch (e) { onMessage('error', e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this highlight?')) return;
    try { await apiCall('DELETE', `menu-highlights/${id}`); onMessage('success', 'Deleted'); refetch(); }
    catch (e) { onMessage('error', e.message); }
  };

  // Swap `order` values between two highlights to reorder them.
  const handleMove = async (item, direction) => {
    const idx = sortedItems.findIndex((i) => i._id === item._id);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= sortedItems.length) return;
    const a = sortedItems[idx];
    const b = sortedItems[target];
    const aOrder = a.order ?? idx;
    const bOrder = b.order ?? target;
    try {
      await Promise.all([
        apiCall('PUT', `menu-highlights/${a._id}`, { order: bOrder }),
        apiCall('PUT', `menu-highlights/${b._id}`, { order: aOrder }),
      ]);
      refetch();
    } catch (e) { onMessage('error', e.message); }
  };

  const Form = ({ value, onChange, onSubmit, onCancel, isEdit }) => (
    <div className="bg-white rounded-xl p-6 shadow-md mb-4 border-l-4 border-[#C9A86C]" data-testid="highlight-form">
      <h3 className="text-lg font-semibold mb-4">{isEdit ? 'Edit Highlight' : 'New Highlight'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Dish Name</label>
          <input
            type="text"
            placeholder="Dish Name"
            value={value.name || ''}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            className="w-full border rounded-lg px-4 py-2"
            data-testid="highlight-name-input"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <select
            value={value.category || ''}
            onChange={(e) => onChange({ ...value, category: e.target.value })}
            className="w-full border rounded-lg px-4 py-2"
            data-testid="highlight-category-select"
          >
            {categories.length === 0 && <option value="">— add a menu item first —</option>}
            {value.category && !categories.includes(value.category) && (
              <option value={value.category}>{value.category}</option>
            )}
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <textarea
            placeholder="Description"
            value={value.description || ''}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            className="w-full border rounded-lg px-4 py-2"
            rows={2}
            data-testid="highlight-description-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Image</label>
          <ImageUploader currentImage={value.image} onUpload={(url) => onChange({ ...value, image: url })} />
          <input
            type="text"
            placeholder="Or paste image URL"
            value={value.image || ''}
            onChange={(e) => onChange({ ...value, image: e.target.value })}
            className="w-full border rounded-lg px-4 py-2 mt-2"
            data-testid="highlight-image-url-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input
            type="checkbox"
            checked={value.is_active !== false}
            onChange={(e) => onChange({ ...value, is_active: e.target.checked })}
          />
          Active (show on homepage)
        </label>
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={onSubmit} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2" data-testid="highlight-save-btn">
          <Save size={18} /> {isEdit ? 'Update' : 'Save'}
        </button>
        <button onClick={onCancel} className="bg-gray-500 text-white px-4 py-2 rounded-lg">Cancel</button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Menu Highlights (Homepage)</h2>
          <p className="text-sm text-gray-500 mt-1">
            {items.length} highlight{items.length === 1 ? '' : 's'}. Use the up/down arrows to change the
            order they appear on the homepage.
          </p>
        </div>
        <button
          onClick={() => setCreating(blank(categories[0] || '', items.length))}
          className="bg-[#C9A86C] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#B8956A]"
          data-testid="add-highlight-btn"
        >
          <Plus size={18} /> Add Highlight
        </button>
      </div>

      {creating && (
        <Form
          value={creating}
          onChange={setCreating}
          onSubmit={() => handleSave(creating, true)}
          onCancel={() => setCreating(null)}
          isEdit={false}
        />
      )}

      <div className="space-y-3">
        {sortedItems.map((item, idx) =>
          editing && editing._id === item._id ? (
            <Form
              key={item._id}
              value={editing}
              onChange={setEditing}
              onSubmit={() => handleSave(editing, false)}
              onCancel={() => setEditing(null)}
              isEdit
            />
          ) : (
            <div
              key={item._id}
              className="bg-white rounded-xl shadow-md flex items-stretch overflow-hidden"
              data-testid={`highlight-card-${item._id}`}
            >
              <div className="flex flex-col items-center justify-center bg-gray-50 px-2 py-3 border-r">
                <button
                  onClick={() => handleMove(item, 'up')}
                  disabled={idx === 0}
                  className="text-gray-500 hover:bg-gray-200 disabled:opacity-25 disabled:cursor-not-allowed p-1 rounded"
                  title="Move up"
                  data-testid={`highlight-move-up-${item._id}`}
                >
                  <ChevronUp size={20} />
                </button>
                <span className="text-[10px] text-gray-400 leading-none my-1">{idx + 1}</span>
                <button
                  onClick={() => handleMove(item, 'down')}
                  disabled={idx === sortedItems.length - 1}
                  className="text-gray-500 hover:bg-gray-200 disabled:opacity-25 disabled:cursor-not-allowed p-1 rounded"
                  title="Move down"
                  data-testid={`highlight-move-down-${item._id}`}
                >
                  <ChevronDown size={20} />
                </button>
              </div>
              <img src={item.image} alt={item.name} className="w-32 h-32 object-cover bg-gray-100 flex-shrink-0" />
              <div className="p-4 flex-1 flex flex-col justify-center min-w-0">
                <span className="text-xs text-[#C9A86C] font-semibold uppercase tracking-wide">{item.category}</span>
                <h3 className="font-semibold mt-1">
                  {item.name}
                  {item.is_active === false && (
                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Hidden</span>
                  )}
                </h3>
                {item.description && (
                  <p className="text-gray-600 text-sm mt-1 line-clamp-2">{item.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1 px-3">
                <button
                  onClick={() => setEditing({ ...item })}
                  className="text-blue-600 hover:bg-blue-50 p-2 rounded flex items-center gap-1 text-sm"
                  data-testid={`edit-highlight-${item._id}`}
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={() => handleDelete(item._id)}
                  className="text-red-600 hover:bg-red-50 p-2 rounded"
                  data-testid={`delete-highlight-${item._id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MenuHighlightsManager;
